import React, { useCallback, useEffect, useState } from 'react';
import Dialog from "../../components/controls/Dialog";
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import * as clientService from '../../services/clientService';
import * as maskingConfigService from '../../services/maskingConfigService';
import useAlert from '../../components/useAlert';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    IconButton, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    Box,
    Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const ClientMaskingConfigsDialog = (props) => {
    const { open, setOpen, selectedRow, setSelectedRow } = props;
    const [clientConfigs, setClientConfigs] = useState([]);
    const [allConfigs, setAllConfigs] = useState([]);
    const [selectedConfigId, setSelectedConfigId] = useState('');

    const {
        Alert,
        status,
        showSuccess,
        showError,
        handleAlertClose
    } = useAlert();

    const fetchAllConfigs = useCallback(() => {
        maskingConfigService.getMaskingConfigs()
            .then(response => {
                setAllConfigs(response.data.items || []);
            })
            .catch(error => {
                showError(error.response?.data?.message || "Error fetching masking configs");
            });
    }, [showError]);

    useEffect(() => {
        if (open && selectedRow) {
            handleAlertClose();
            setClientConfigs(
                selectedRow.maskingConfig
                    ? (Array.isArray(selectedRow.maskingConfig)
                        ? selectedRow.maskingConfig
                        : Object.values(selectedRow.maskingConfig))
                    : []
            );
            fetchAllConfigs();
        }
    }, [open, selectedRow, fetchAllConfigs, handleAlertClose]);

    const handleClose = () => {
        setOpen(false);
        setSelectedRow(null);
    };

    const handleBindConfig = () => {
        if (!selectedConfigId) return;
        clientService.bindClientMaskingConfig(selectedRow.id, selectedConfigId, clientConfigs)
            .then(() => {
                showSuccess("Masking config bound successfully");
                if (props.getProjectClients) {
                    props.getProjectClients();
                } else {
                    const newConfig = allConfigs.find(config => config.id === selectedConfigId);
                    if (newConfig) {
                        setClientConfigs((prev) => [...prev, newConfig]);
                    }
                }
                setSelectedConfigId('');
            })
            .catch(error => {
                showError(error.response?.data?.message || "Error binding masking config");
            });
    };

    const handleUnbindConfig = (configId) => {
        clientService.unbindClientMaskingConfig(selectedRow.id, configId, clientConfigs)
            .then(() => {
                showSuccess("Masking config unbound successfully");
                if (props.getProjectClients) {
                    props.getProjectClients();
                } else {
                    setClientConfigs((prev) => prev.filter(config => config.id !== configId));
                }
            })
            .catch(error => {
                showError(error.response?.data?.message || "Error unbinding masking config");
            });
    };

    return (
        <React.Fragment>
            {status ? <Alert /> : null}
            <Dialog
                title={`Manage Masking Configs for Client: ${selectedRow?.name}`}
                open={open}
                handleClose={handleClose}
                maxWidth="md"
                fullWidth
            >
                <DialogContent>
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-end' }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Add Masking Config</InputLabel>
                            <Select
                                value={selectedConfigId}
                                label="Add Masking Config"
                                onChange={(e) => setSelectedConfigId(e.target.value)}
                            >
                                {allConfigs
                                    .filter(c => !clientConfigs.find(cc => cc.id === c.id))
                                    .map(c => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.id} ({c.regExp})
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        <Button 
                            variant="contained" 
                            startIcon={<AddIcon />}
                            onClick={handleBindConfig}
                            disabled={!selectedConfigId}
                        >
                            Add
                        </Button>
                    </Box>

                    <Typography variant="h6" gutterBottom>
                        Assigned Masking Configs
                    </Typography>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Config ID</TableCell>
                                    <TableCell>Regular Expression</TableCell>
                                    <TableCell>Replacement</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clientConfigs.map((config) => (
                                    <TableRow key={config.id}>
                                        <TableCell>{config.id}</TableCell>
                                        <TableCell>{config.regExp}</TableCell>
                                        <TableCell>{config.replacement}</TableCell>
                                        <TableCell align="center">
                                            <IconButton 
                                                size="small" 
                                                color="error"
                                                onClick={() => handleUnbindConfig(config.id)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {clientConfigs.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            No masking configs assigned to this client.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};

export default ClientMaskingConfigsDialog;
